import { useEffect, useState } from "react";

import { Button } from "../../components/Button";
import { ConfirmSheet } from "../../components/ConfirmSheet";
import { I } from "../../components/icons";
import { Notice } from "../../components/Notice";
import { NavRow, RowGroup } from "../../components/Rows";
import { ago } from "../../components/StepsRefresh";
import { Tag } from "../../components/Tag";
import { addDays, todayStr } from "../../domain/date";
import { strideMeters } from "../../domain/energy";
import { isAndroid } from "../../lib/device";
import { useKaya } from "../../store/kayaStore";
import { api, type ApiKeyInfo } from "../../sync/api";
import SettingsPage from "./SettingsPage";

const GUIDE = "https://github.com/connectwithub/kaya/blob/main/docs/step-sync.md";

function shortDate(ms: number): string {
  return new Date(ms).toLocaleDateString(undefined, {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
}

/**
 * Everything to do with getting a step count out of the phone and into Kaya: the status (which
 * links to Today › Activity, where the refresh lives), the Android app, the keys an automation
 * posts with, and the stride that turns a distance into steps.
 *
 * No signed-out branch: the ADR-020 gate means nothing renders without an approved account.
 */
export default function StepCounting() {
  const { S, update, showToast, user, stepsReportedAt } = useKaya();
  const [keys, setKeys] = useState<ApiKeyInfo[]>([]);
  /** Held only long enough for the user to copy it; the server never returns it again. */
  const [newKey, setNewKey] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [ask, setAsk] = useState<{ kind: "revoke"; key: ApiKeyInfo } | { kind: "stride" } | null>(null);

  useEffect(() => {
    if (!user) return;
    api
      .listKeys()
      .then((r) => setKeys(r.keys))
      .catch(() => setKeys([]));
  }, [user]);

  const since = addDays(todayStr(), -3);
  const counting = Object.entries(S.logs).some(
    ([ds, L]) => ds >= since && typeof L.deviceSteps === "number" && L.deviceSteps > 0,
  );
  const when = ago(stepsReportedAt);

  async function makeKey() {
    setBusy(true);
    try {
      const created = await api.createKey("Step sync");
      setNewKey(created.key);
      setKeys(await api.listKeys().then((r) => r.keys));
    } catch (e) {
      showToast(e instanceof Error ? e.message : "Could not create a key");
    } finally {
      setBusy(false);
    }
  }

  async function revokeKey(id: string) {
    setAsk(null);
    try {
      await api.deleteKey(id);
      setKeys(keys.filter((k) => k.id !== id));
      showToast("Key revoked");
    } catch {
      showToast("Could not revoke that key");
    }
  }

  function copyKey(k: string) {
    navigator.clipboard
      ?.writeText(k)
      .then(() => showToast("Key copied"))
      .catch(() => showToast("Select and copy it manually"));
  }

  function download() {
    // A real anchor with `download`, as before: a plain navigation to an .apk opens a blank tab in
    // some Android browsers instead of saving it.
    const a = document.createElement("a");
    a.href = "/kaya-steps.apk";
    a.download = "kaya-steps.apk";
    a.click();
  }

  const samples = S.profile.strideSamples ?? 0;
  const stride = `${strideMeters(S).toFixed(2)} m`;

  return (
    <SettingsPage
      title="Step counting"
      intro="A browser cannot read the step counter, so something on the phone posts it instead."
    >
      <RowGroup>
        <NavRow
          icon={I.footsteps}
          tone={counting ? "success" : "default"}
          title={counting ? "Counting" : "Not set up"}
          subtitle={when ? `Phone reported ${when}` : "No count from the phone yet"}
          to="/today/activity"
        />
      </RowGroup>

      {isAndroid() ? (
        <RowGroup heading="Kaya Steps app">
          {/* The app's own settings screen. If the scheme is not handled — the app is not
              installed, or the browser blocks it — long-pressing the Kaya icon offers the same
              screen as a launcher shortcut. */}
          <NavRow
            icon={I.phone}
            title="Open step settings"
            subtitle="Or long-press the Kaya icon › Step settings"
            onClick={() => {
              window.location.href = "kayasteps://settings";
            }}
          />
          <NavRow
            kind="action"
            icon={I.dl}
            title="Download the app"
            subtitle="Android warns it is not from the Play Store — expected for a self-hosted app"
            action={
              <Button variant="ghost" onClick={download}>
                Download
              </Button>
            }
          />
        </RowGroup>
      ) : null}

      {newKey ? (
        <Notice
          tone="caution"
          icon={I.lock}
          primary={{ label: "Copy key", onClick: () => copyKey(newKey) }}
          secondary={{ label: "Done", onClick: () => setNewKey(null) }}
        >
          Copy this now — it is not shown again. <code className="you-key">{newKey}</code>
        </Notice>
      ) : null}

      <RowGroup heading="Automations">
        {keys.map((k) => (
          <NavRow
            key={k.id}
            kind="action"
            icon={I.lock}
            title={k.name}
            subtitle={`${k.prefix}… · ${k.lastUsedAt ? "used " + shortDate(k.lastUsedAt) : "unused"}`}
            action={
              <Button
                variant="danger"
                onClick={() => setAsk({ kind: "revoke", key: k })}
                ariaLabel={`Revoke ${k.name}`}
              >
                Revoke
              </Button>
            }
          />
        ))}
        <NavRow
          kind="action"
          icon={I.plus}
          title={keys.length ? "Another key" : "Automation key"}
          subtitle={
            keys.length ? "One per phone or automation" : "None yet. Create one, then paste it into the counter app"
          }
          action={
            <Button variant="ghost" busy={busy} onClick={() => void makeKey()}>
              Create key
            </Button>
          }
        />
        <NavRow
          icon={I.book}
          title="Set up a phone automation"
          subtitle="The setup guide, with Tasker and shortcut recipes"
          onClick={() => window.open(GUIDE, "_blank", "noopener,noreferrer")}
        />
      </RowGroup>

      {/* Stride is what turns a treadmill distance into a step count, so it is worth saying out
          loud whether it is measured or assumed. */}
      <RowGroup heading="Stride">
        {samples ? (
          <NavRow
            kind="action"
            icon={I.walk}
            title={
              <>
                {stride} <Tag tone="measured" />
              </>
            }
            subtitle={`From ${samples} treadmill ${samples === 1 ? "walk" : "walks"} with the phone on you`}
            action={
              <Button variant="ghost" onClick={() => setAsk({ kind: "stride" })}>
                Reset
              </Button>
            }
          />
        ) : (
          <NavRow
            kind="value"
            icon={I.walk}
            title={
              <>
                ~{stride} <Tag tone="est" />
              </>
            }
            subtitle="From your height. A treadmill walk of 300 m or more with the phone on you measures it."
          />
        )}
      </RowGroup>
      {ask?.kind === "revoke" ? (
        <ConfirmSheet
          title={`Revoke ${ask.key.name}?`}
          sub="Any automation using it stops working immediately."
          confirm={{
            label: "Revoke key",
            onClick: () => void revokeKey(ask.key.id),
          }}
          onClose={() => setAsk(null)}
        />
      ) : null}
      {ask?.kind === "stride" ? (
        <ConfirmSheet
          title="Forget the measured stride?"
          sub="Distance goes back to the estimate from your height."
          confirm={{
            label: "Reset stride",
            onClick: () => {
              update((d) => {
                delete d.profile.strideM;
                delete d.profile.strideSamples;
              });
              setAsk(null);
              showToast("Stride reset");
            },
          }}
          onClose={() => setAsk(null)}
        />
      ) : null}
    </SettingsPage>
  );
}
